const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export async function getCollectionProducts(slug, sort = 'popular') {
  return request(`/api/collections/${slug}/products?sort=${sort}`);
}

export function getToken() {
  return localStorage.getItem('nq_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  const data = await res.json();

  // Token expired — clear session and notify the app
  if (res.status === 401 && data.code === 'TOKEN_EXPIRED') {
    logout();
    if (typeof window.__nq_onTokenExpired === 'function') {
      window.__nq_onTokenExpired();
    }
    throw new Error('Your session has expired. Please log in again.');
  }

  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

/* ── Auth ── */
export async function login(email, password) {
  const data = await request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  localStorage.setItem('nq_token', data.token);
  localStorage.setItem('nq_user', JSON.stringify(data.user));
  return data.user;
}

export async function sendRegistrationOtp(email) {
  return request('/api/auth/send-registration-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyRegistrationOtp(email, otp) {
  return request('/api/auth/verify-registration-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

export async function register(firstName, lastName, email, password, regToken) {
  const data = await request('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ firstName, lastName, email, password, regToken }),
  });
  localStorage.setItem('nq_token', data.token);
  localStorage.setItem('nq_user', JSON.stringify(data.user));
  return data.user;
}

export function logout() {
  localStorage.removeItem('nq_token');
  localStorage.removeItem('nq_user');
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem('nq_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function getMe() {
  return request('/api/auth/me');
}

export async function updateProfile({ firstName, lastName, phone }) {
  const data = await request('/api/auth/me', {
    method: 'PUT',
    body: JSON.stringify({ firstName, lastName, phone }),
  });
  // Keep the cached user (used for navbar/sidebar greeting) in sync.
  // Merge onto the existing stored shape ({id, firstName, lastName, email, isAdmin})
  // rather than replacing it with the raw Mongo doc (which uses _id, not id).
  const stored = getStoredUser() || {};
  const merged = { ...stored, firstName: data.firstName, lastName: data.lastName, email: data.email, phone: data.phone || '' };
  try { localStorage.setItem('nq_user', JSON.stringify(merged)); } catch { /* ignore storage errors */ }
  return merged;
}

export async function changePassword(currentPassword, newPassword) {
  return request('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/* ── Forgot-password OTP flow ─────────────────────────────────────────── */
export async function forgotPassword(email) {
  return request('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyOtp(email, otp) {
  return request('/api/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

export async function resetPassword(resetToken, newPassword) {
  return request('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ resetToken, newPassword }),
  });
}

/* ── Guest contact info (Shopify-style "remember me on this device") ──────
   Guests have no account to save to, so we remember their last-used email
   and phone in localStorage on this browser, and prefill the checkout
   contact fields with it next time — same idea as Shopify's "Save my
   information for a faster checkout next time" for one-time/guest buyers. */
const GUEST_CONTACT_KEY = 'nq_guest_contact';

export function getGuestContact() {
  try {
    const raw = localStorage.getItem(GUEST_CONTACT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveGuestContact(data) {
  try {
    const {
      email = '', phone = '', firstName = '', lastName = '',
      address = '', apartment = '', city = '', state = '', pincode = '',
      deliveryLocation = '', lat = null, lng = null,
    } = data || {};
    localStorage.setItem(GUEST_CONTACT_KEY, JSON.stringify({
      email, phone, firstName, lastName,
      address, apartment, city, state, pincode,
      deliveryLocation, lat, lng,
    }));
  } catch { /* ignore storage errors */ }
}

export function clearGuestContact() {
  localStorage.removeItem(GUEST_CONTACT_KEY);
}

/* ── Saved addresses ── */
export async function getAddresses() {
  return request('/api/users/addresses');
}

export async function addAddress(address) {
  return request('/api/users/addresses', {
    method: 'POST',
    body: JSON.stringify(address),
  });
}

export async function updateAddress(id, address) {
  return request(`/api/users/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(address),
  });
}

export async function deleteAddress(id) {
  return request(`/api/users/addresses/${id}`, { method: 'DELETE' });
}

/* ── Wishlist ──────────────────────────────────────────────────────────────
   There's no wishlist backend yet, so this is persisted to localStorage —
   same pattern already used for the cart and applied coupon. Keyed per
   browser, not per account; swap for real API calls once a wishlist
   endpoint exists server-side. */
const WISHLIST_KEY = 'nq_wishlist';

export function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveWishlist(items) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event('nq_wishlist_change'));
  return items;
}

export function isInWishlist(productId) {
  return getWishlist().some(i => i.id === productId);
}

export function toggleWishlist(product) {
  const list = getWishlist();
  const exists = list.some(i => i.id === product.id);
  const next = exists ? list.filter(i => i.id !== product.id) : [...list, product];
  return saveWishlist(next);
}

export function removeFromWishlist(productId) {
  return saveWishlist(getWishlist().filter(i => i.id !== productId));
}

/* ── Orders ── */
export async function placeOrder({ items, address, payment, promoCode, deliveryCoords }) {
  return request('/api/orders', {
    method: 'POST',
    body: JSON.stringify({ items, address, payment, promoCode, deliveryCoords }),
  });
}

export async function getOrders(page = 1) {
  const data = await request(`/api/orders?page=${page}&limit=20`);
  // Support both old array response and new paginated response
  return Array.isArray(data) ? data : (data.orders || []);
}

export async function getOrder(orderId) {
  return request(`/api/orders/${orderId}`);
}

// Unauthenticated lookup used only by the OrderConfirmationPage when the user
// lands there via a real browser redirect (PayU) instead of in-app navigation.
export async function getOrderConfirmation(orderId) {
  return request(`/api/orders/confirmation/${orderId}`);
}

// Public "Track your order" lookup — no login required. Customer proves
// ownership with the orderId + the email the order was placed under.
export async function trackOrder(orderId, email) {
  return request('/api/orders/track', {
    method: 'POST',
    body: JSON.stringify({ orderId, email }),
  });
}

// One-click "Track Order" link from order-notification emails — no email
// or login needed, the unguessable slug itself proves ownership.
export async function trackOrderBySlug(slug) {
  return request(`/api/orders/track/${slug}`);
}

/* ── Products ── */
export async function getProducts(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/products${qs ? `?${qs}` : ''}`);
}

/* ── Reviews ── */
export async function getReviewableItems() {
  return request('/api/reviews/reviewable');
}

export async function getMyReviews() {
  return request('/api/reviews/mine');
}

export async function getProductReviews(productId, page = 1) {
  return request(`/api/reviews/product/${productId}?page=${page}&limit=10`);
}

export async function uploadReviewImage(file, reviewSlug = null) {
  const token = getToken();
  const formData = new FormData();
  formData.append('image', file);
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  else if (reviewSlug) headers['X-Review-Slug'] = reviewSlug;
  const res = await fetch(`${BASE}/api/reviews/upload-image`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Image upload failed');
  return data; // { url, public_id }
}

export async function submitReview({ orderId, orderItemIndex, rating, title, comment, images, reviewSlug = null }) {
  if (reviewSlug) {
    // Slug-link flow: no login needed — the slug itself is the auth
    const res = await fetch(`${BASE}/api/reviews/by-slug`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: reviewSlug, orderItemIndex, rating, title, comment, images }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to submit review');
    return data;
  }
  return request('/api/reviews', {
    method: 'POST',
    body: JSON.stringify({ orderId, orderItemIndex, rating, title, comment, images }),
  });
}

export async function deleteReview(reviewId) {
  return request(`/api/reviews/${reviewId}`, { method: 'DELETE' });
}

export async function getOrderByReviewSlug(slug) {
  return request(`/api/reviews/by-slug/${slug}`);
}

/* ── Token expiry handler ── */
// Call this in your App to be notified when session expires
export function onTokenExpired(callback) {
  window.__nq_onTokenExpired = callback;
}

/* ── Coupons (public — real DB) ── */
export async function getPublicCoupons() {
  return request('/api/coupons');
}

export async function validateCoupon(code, orderTotal) {
  return request('/api/coupons/validate', {
    method: 'POST',
    body: JSON.stringify({ code, orderTotal }),
  });
}

/* ── Razorpay payments ── */
export async function createRazorpayOrder({ items, address, promoCode }) {
  return request('/api/payments/razorpay/create-order', {
    method: 'POST',
    body: JSON.stringify({ items, address, promoCode }),
  });
}

export async function verifyRazorpayPayment({
  razorpay_order_id, razorpay_payment_id, razorpay_signature,
  items, address, promoCode, deliveryCoords,
}) {
  return request('/api/payments/razorpay/verify', {
    method: 'POST',
    body: JSON.stringify({
      razorpay_order_id, razorpay_payment_id, razorpay_signature,
      items, address, promoCode, deliveryCoords,
    }),
  });
}

// Loads the Razorpay Checkout script once and caches the promise so repeated
// checkout attempts in the same session don't re-fetch it.
let razorpayScriptPromise = null;
export function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => { razorpayScriptPromise = null; reject(new Error('Failed to load Razorpay checkout script')); };
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

/* ── PayU payments ── */
// PayU is a redirect/hosted-page flow: the server returns an action URL +
// signed form fields, and the browser is sent there via a real <form> POST
// (not fetch) so PayU's page receives a normal top-level navigation.
export async function initiatePayuPayment({ items, address, billingAddress, promoCode, deliveryCoords }) {
  return request('/api/payments/payu/initiate', {
    method: 'POST',
    body: JSON.stringify({ items, address, billingAddress, promoCode, deliveryCoords }),
  });
}

export function redirectToPayu({ action, fields }) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value ?? '';
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}
/* ── Site Settings ── */
export async function getSiteSettings() {
  return request('/api/site-settings');
}

/* ── Shipping Settings (public, read-only) ── */
export async function getShippingSettings() {
  return request('/api/shipping-settings');
}

/* ── Payment Settings (public, read-only — which gateways are enabled) ── */
export async function getPaymentSettings() {
  return request('/api/payment-settings');
}

/* ── Blog (Garden Journal) ── */
export async function getBlogPosts({ category, q, limit } = {}) {
  const params = new URLSearchParams();
  if (category && category !== 'all') params.set('category', category);
  if (q) params.set('q', q);
  if (limit) params.set('limit', limit);
  const qs = params.toString();
  return request(`/api/blogs${qs ? `?${qs}` : ''}`);
}

export async function getBlogPost(slug) {
  return request(`/api/blogs/${slug}`);
}

export async function getRelatedBlogPosts(slug, count = 3) {
  return request(`/api/blogs/${slug}/related?count=${count}`);
}

/* ── Abandoned Cart Sync ── */

// Syncs the current cart to the server (for logged-in users only).
// checkoutStep: 'cart' | 'checkout'
export async function syncCart(items, checkoutStep = 'cart') {
  return request('/api/carts/sync', {
    method: 'POST',
    body: JSON.stringify({ items, checkoutStep }),
  });
}

// Called from CheckoutPage when a guest types a valid email.
// Captures the cart so it shows up in admin abandoned carts.
export async function guestSyncCart({ items, email, firstName, lastName }) {
  return request('/api/carts/guest-sync', {
    method: 'POST',
    body: JSON.stringify({ items, email, firstName, lastName, checkoutStep: 'checkout' }),
  });
}

// Called after an order is placed to remove the server-side cart record.
export async function clearServerCart() {
  return request('/api/carts/clear', { method: 'DELETE' });
}
/* ── Bundles ── */
export async function getBundles() {
  return request('/api/bundles');
}

export async function getBundleBySlug(slug) {
  return request(`/api/bundles/${slug}`);
}

export async function getProductsForBundle(query = '') {
  const q = query ? `?search=${encodeURIComponent(query)}&sort=popular` : '?sort=popular';
  return request(`/api/products${q}`);
}
